import { useState } from 'react';
import { convertToMp4 } from '../../lib/video-converter';
import { useThemeColors } from '../../hooks/useThemeColors';
import { trackConversionDialogResult } from '../../lib/analytics';

interface ConversionDialogProps {
  file: File;
  onConfirm: (convertedFile: File) => void;
  onCancel: () => void;
}

type DialogState = 'confirm' | 'converting' | 'error';

export default function ConversionDialog({ file, onConfirm, onCancel }: ConversionDialogProps) {
  const c = useThemeColors();
  const [state, setState] = useState<DialogState>('confirm');
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const ext = '.' + file.name.split('.').pop()?.toUpperCase();

  async function handleConvert() {
    setState('converting');
    setProgress(0);
    try {
      const converted = await convertToMp4(file, (p) => setProgress(p));
      setProgress(100);
      await new Promise(r => setTimeout(r, 300)); // brief flash of 100%
      trackConversionDialogResult({ result: 'converted', file_extension: ext.toLowerCase(), file_size_bytes: file.size });
      onConfirm(converted);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[FFmpeg] Conversion error:', err);
      trackConversionDialogResult({ result: 'error', file_extension: ext.toLowerCase(), file_size_bytes: file.size });
      setErrorMsg(msg || 'Unknown error occurred.');
      setState('error');
    }
  }

  function handleCancel() {
    if (state === 'confirm') {
      trackConversionDialogResult({ result: 'cancelled', file_extension: ext.toLowerCase(), file_size_bytes: file.size });
    }
    onCancel();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: c.bgSecondary,
        border: `1px solid ${c.border}`,
        borderRadius: 4,
        maxWidth: 480,
        width: '100%',
        padding: '28px 28px 24px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        {state === 'confirm' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 24 }}>🔄</span>
              <h2 style={{ color: c.textPrimary, fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
                Convert to MP4?
              </h2>
            </div>
            <p style={{ color: c.textMuted, fontSize: 13, lineHeight: '1.6', margin: '0 0 24px' }}>
              <strong style={{ color: c.textPrimary }}>{file.name}</strong> is a {ext} file. Primate Vision requires MP4.
              {' '}We'll convert it in your browser — your original file won't be changed.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleConvert}
                style={{
                  flex: 1, background: c.signal, color: '#FFFFFF',
                  border: 'none', borderRadius: 3, padding: '11px 16px',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  transition: 'background 150ms',
                }}
              >
                Convert &amp; Upload
              </button>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1, background: 'transparent', color: c.textMuted,
                  border: `1px solid ${c.border}`, borderRadius: 3, padding: '11px 16px',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  transition: 'border-color 150ms, color 150ms',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {state === 'converting' && (
          <>
            <h2 style={{ color: c.textPrimary, fontSize: 18, fontWeight: 700, margin: '0 0 16px', letterSpacing: '-0.01em' }}>
              Converting…
            </h2>
            <p style={{ color: c.textMuted, fontSize: 13, lineHeight: '1.6', margin: '0 0 20px' }}>
              Converting <strong style={{ color: c.textPrimary }}>{file.name}</strong> to MP4. This may take a moment.
            </p>
            <div style={{ marginBottom: 8 }}>
              <div style={{ height: 6, background: c.border, borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                {progress > 0 ? (
                  // Determinate progress bar
                  <div style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: c.signal,
                    borderRadius: 3,
                    transition: 'width 300ms ease',
                  }} />
                ) : (
                  // Indeterminate shimmer — FFmpeg may not report progress for short clips
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: `linear-gradient(90deg, transparent 0%, ${c.signal} 40%, #4A7AFF 60%, transparent 100%)`,
                    animation: 'cvt-shimmer 1.4s ease-in-out infinite',
                    backgroundSize: '200% 100%',
                  }} />
                )}
              </div>
              <p style={{
                color: c.textMuted, fontSize: 11, margin: '6px 0 0',
                fontFamily: '"JetBrains Mono", monospace', textAlign: 'right' as const,
              }}>
                {progress > 0 ? `${progress}%` : 'Converting…'}
              </p>
            </div>
            <style>{`
              @keyframes cvt-shimmer {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
              }
            `}</style>
          </>
        )}

        {state === 'error' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 24 }}>⚠️</span>
              <h2 style={{ color: '#DC2626', fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
                Conversion failed
              </h2>
            </div>
            <p style={{ color: c.textMuted, fontSize: 13, lineHeight: '1.6', margin: '0 0 8px' }}>
              We couldn't convert this file. {errorMsg}
            </p>
            <p style={{ color: c.textMuted, fontSize: 13, lineHeight: '1.6', margin: '0 0 24px' }}>
              You can try uploading a different format, or convert the file manually using a tool like{' '}
              <a href="https://handbrake.fr" target="_blank" rel="noopener noreferrer"
                style={{ color: c.signal, textDecoration: 'none', fontWeight: 600 }}>
                HandBrake
              </a>
              {' '}(free) or{' '}
              <a href="https://cloudconvert.com" target="_blank" rel="noopener noreferrer"
                style={{ color: c.signal, textDecoration: 'none', fontWeight: 600 }}>
                CloudConvert
              </a>.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={onCancel}
                style={{
                  flex: 1, background: c.signal, color: '#FFFFFF',
                  border: 'none', borderRadius: 3, padding: '11px 16px',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}
              >
                Try another file
              </button>
              <button
                onClick={onCancel}
                style={{
                  flex: 1, background: 'transparent', color: c.textMuted,
                  border: `1px solid ${c.border}`, borderRadius: 3, padding: '11px 16px',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
